import { CatalogFilterState } from "../types/reducers";
import { Alert } from "../types/api/models/alert";
import { CatalogItem } from "../types/api/models/catalog";

export const getCatalogItemTimestamp = (
  item: CatalogItem,
): Date | string | undefined =>
  item.source === "detection"
    ? item.capture_created_at || item.created_at
    : item.created_at;

export const getAlertTimestamp = (
  alert: Pick<Alert, "context" | "created_at">,
): Date => alert?.context?.data?.timestamp || alert?.created_at;

/**
 * The mesh capture a mesh_dirty alert links to: the latest of the captures that
 * raised it, which the notification service lists oldest first. Undefined for
 * any other alert, and for a mesh_dirty alert raised before the service began
 * recording capture ids.
 */
export const getAlertMeshCaptureId = (
  alert: Pick<Alert, "msg_type" | "context">,
): string | undefined => {
  if (alert?.msg_type !== "mesh_dirty") return undefined;
  const ids = alert.context?.data?.capture_ids;
  if (!Array.isArray(ids)) return undefined;
  const latest = ids[ids.length - 1];
  return typeof latest === "string" && latest ? latest : undefined;
};

export const formatDate = (
  createdAt: string | Date | undefined,
  options?: { dateOnly?: boolean },
): string => {
  if (!createdAt) return "";
  // a trailing z or an explicit +hh:mm/-hh:mm offset means the string is already zoned
  const hasOffset = /(z|[+-]\d{2}:?\d{2})$/i.test(createdAt as string);
  const d =
    createdAt instanceof Date
      ? createdAt
      : typeof createdAt === "string"
        ? new Date(createdAt + (hasOffset ? "" : "+00:00"))
        : new Date(createdAt);
  if (isNaN(d.getTime())) return "";
  const formatOptions: Intl.DateTimeFormatOptions = options?.dateOnly
    ? { day: "numeric", month: "short", year: "numeric" }
    : {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
  return d
    .toLocaleString(navigator?.language || "en-US", formatOptions)
    .replace(" at", ",");
};

export const parseSpeciesToList = (
  species: CatalogFilterState["species"],
): string[] => {
  const result: string[] = [];
  Object.entries(species).forEach(([genus, data]) => {
    const subSpeciesKeys = Object.keys(data.species);
    if (subSpeciesKeys.length === 0) {
      if (data.selected) {
        result.push(genus);
      }
    } else {
      subSpeciesKeys.forEach((speciesName) => {
        if (data.species[speciesName]) {
          result.push(`${genus} ${speciesName}`);
        }
      });
    }
  });
  return result;
};

export const toggleSpeciesFilter = (
  currentSpecies: CatalogFilterState["species"],
  genus: string,
  speciesName?: string,
  allSpeciesForGenus: string[] = [],
): CatalogFilterState["species"] => {
  const newSpecies = { ...currentSpecies };
  if (!newSpecies[genus]) {
    const initialSpecies = allSpeciesForGenus.reduce(
      (acc, s) => {
        acc[s] = false;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    newSpecies[genus] = { selected: false, species: initialSpecies };
  }

  if (speciesName) {
    newSpecies[genus] = {
      ...newSpecies[genus],
      species: {
        ...newSpecies[genus].species,
        [speciesName]: !newSpecies[genus].species[speciesName],
      },
    };
    const anySpeciesSelected = Object.values(newSpecies[genus].species).some(
      (val) => val,
    );
    newSpecies[genus].selected = anySpeciesSelected;
  } else {
    const newSelected = !newSpecies[genus].selected;
    const speciesKeys =
      Object.keys(newSpecies[genus].species).length > 0
        ? Object.keys(newSpecies[genus].species)
        : allSpeciesForGenus;
    newSpecies[genus] = {
      ...newSpecies[genus],
      selected: newSelected,
      species: speciesKeys.reduce(
        (acc, key) => {
          acc[key] = newSelected;
          return acc;
        },
        {} as { [key: string]: boolean },
      ),
    };
  }
  return newSpecies;
};

export const generateUniqueCatalogId = (source: string, id: string): string => {
  if (source === "capture") {
    return `CAPTURE_${id}`;
  }
  return `SPECIMEN_${id}`;
};

export const fetchImageAsBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to fetch image from ${url}`);
  }
  return await response.blob();
};
